import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Plus, Pencil, Trash2, FileText, Filter, X,
    Sparkles, Code2, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
    Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { useTestCasesWithMeta, useDeleteTestCase } from '../api/useTestCases'
import { useProjects } from '@/features/projects/api/useProjects'
import { TestImportance, ExecutionType } from '@/types/testcase.types'
import type { TestCase, TestCaseMemberships } from '@/types/testcase.types'
import { TestCaseDialog } from '../components/TestCaseDialog'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import { logger } from '@/lib/utils/logger'

const ARCHIVE_EXTENSIONS = ['.zip', '.tar', '.tar.gz', '.tgz']
const isArchiveFile = (file: File) =>
    ARCHIVE_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))

const ARCHITECTURAL_LAYERS = [
    'UI/UX',
    'API/Middleware',
    'Backend',
    'LLM',
]

export default function TestCasesPage() {
    const navigate = useNavigate()
    const [selectedProjectId, setSelectedProjectId] = useState<string>('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const pageSize = 50

    const { data: testcasesResponse, isLoading, error } = useTestCasesWithMeta(
        selectedProjectId && selectedProjectId !== 'all'
            ? { project_id: parseInt(selectedProjectId, 10), limit: pageSize, offset: (currentPage - 1) * pageSize }
            : { limit: pageSize, offset: (currentPage - 1) * pageSize }
    )
    const { data: projects } = useProjects()
    const deleteMutation = useDeleteTestCase()

    const testcases = testcasesResponse?.testcases || []
    const totalCount = testcasesResponse?.total || 0
    const totalPages = Math.ceil(totalCount / pageSize)

    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
    const [selectedTestCase, setSelectedTestCase] = useState<TestCase | undefined>()

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [testCaseToDelete, setTestCaseToDelete] = useState<{ id: number; name: string } | null>(null)

    // AI generation dialog state
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false)
    const [genProjectId, setGenProjectId] = useState<string>('')
    const [genLanguage, setGenLanguage] = useState<string>('python')
    const [genLayer, setGenLayer] = useState<string>('')
    const [genFile, setGenFile] = useState<File | null>(null)
    const [genEdgeCases, setGenEdgeCases] = useState(true)
    const [genNegativeTests, setGenNegativeTests] = useState(true)
    const [isGenerating, setIsGenerating] = useState(false)

    // Automation dialog state
    const [automationDialogOpen, setAutomationDialogOpen] = useState(false)
    const [retroProjectId, setRetroProjectId] = useState<string>('')
    const [retroLanguage, setRetroLanguage] = useState<string>('python')
    const [isGeneratingCode, setIsGeneratingCode] = useState(false)

    const [membershipsCache, setMembershipsCache] = useState<Map<number, TestCaseMemberships>>(new Map())
    const [loadingMemberships, setLoadingMemberships] = useState<Set<number>>(new Set())

    const filteredTestCases = testcases.filter((tc) => {
        if (!searchQuery) return true
        const q = searchQuery.toLowerCase()
        return (
            tc.name.toLowerCase().includes(q) ||
            tc.summary?.toLowerCase().includes(q) ||
            tc.author_name?.toLowerCase().includes(q)
        )
    })

    useEffect(() => {
        if (!filteredTestCases || filteredTestCases.length === 0) return
        filteredTestCases.forEach((tc) => {
            if (membershipsCache.has(tc.id) || loadingMemberships.has(tc.id)) return
            setLoadingMemberships((prev) => new Set(prev).add(tc.id))
            apiClient
                .get<{ suites: any[]; projects_direct: any[]; projects_via_suites: any[] }>(
                    `/api/hierarchy/testcases/${tc.id}/memberships`
                )
                .then((response) => {
                    setMembershipsCache((prev) => new Map(prev).set(tc.id, response.data))
                })
                .catch((err) => {
                    logger.error('Failed to fetch memberships for test case', { testcaseId: tc.id, error: err })
                })
                .finally(() => {
                    setLoadingMemberships((prev) => {
                        const next = new Set(prev)
                        next.delete(tc.id)
                        return next
                    })
                })
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredTestCases])

    const getTestFramework = (language: string): string => {
        const m: Record<string, string> = {
            rust: 'cargo-test', python: 'pytest', javascript: 'jest', typescript: 'jest',
            java: 'junit', go: 'go-test', ruby: 'rspec', php: 'phpunit',
            csharp: 'nunit', c: 'ctest', cpp: 'gtest',
        }
        return m[language] || 'pytest'
    }

    const handleCreate = () => {
        setDialogMode('create')
        setSelectedTestCase(undefined)
        setDialogOpen(true)
    }

    const handleEdit = (tc: TestCase) => {
        setDialogMode('edit')
        setSelectedTestCase(tc)
        setDialogOpen(true)
    }

    const handleDeleteClick = (id: number, name: string) => {
        setTestCaseToDelete({ id, name })
        setDeleteConfirmOpen(true)
    }

    const handleDeleteConfirm = async () => {
        if (!testCaseToDelete) return
        try {
            await deleteMutation.mutateAsync(testCaseToDelete.id)
            toast.success(`"${testCaseToDelete.name}" deleted`)
        } catch (err) {
            logger.error('Delete test case failed', { error: err })
            toast.error('Failed to delete test case')
        } finally {
            setDeleteConfirmOpen(false)
            setTestCaseToDelete(null)
        }
    }

    const handleGenerateDialogClose = (open: boolean) => {
        if (!open) {
            setGenProjectId('')
            setGenFile(null)
            setGenLanguage('python')
            setGenLayer('')
            setGenEdgeCases(true)
            setGenNegativeTests(true)
            setIsGenerating(false)
        }
        setGenerateDialogOpen(open)
    }

    // openapi: POST /api/async/code-analysis → 202
    const handleGenerateTests = async () => {
        if (!genProjectId) { toast.error('Please select a project'); return }
        if (!genLayer) { toast.error('Please select an architectural layer'); return }
        if (!genFile) { toast.error('Please select a source file'); return }
        if (isArchiveFile(genFile)) {
            toast.error('Archive files must be uploaded via the Requirements page.')
            return
        }
        setIsGenerating(true)
        try {
            const content = await genFile.text()
            await apiClient.post(
                API_CONFIG.ENDPOINTS.ASYNC?.CODE_ANALYSIS || '/api/async/code-analysis',
                {
                    project_id: parseInt(genProjectId, 10),
                    code_files: [{ [genFile.name]: content }],
                    language: genLanguage,
                    layer: genLayer,
                    test_framework: getTestFramework(genLanguage),
                    generation_type: 'unit',
                    include_edge_cases: genEdgeCases,
                    include_negative_tests: genNegativeTests,
                }
            )
            toast.success('Test generation job submitted. Results will appear shortly.')
            handleGenerateDialogClose(false)
        } catch (err) {
            logger.error('Test generation submission failed', { error: err })
            toast.error('Failed to submit test generation job')
        } finally {
            setIsGenerating(false)
        }
    }

    const handleGenerateAutomation = async () => {
        if (!retroProjectId) { toast.error('Select a project'); return }
        setIsGeneratingCode(true)
        try {
            await apiClient.post(
                API_CONFIG.ENDPOINTS.ASYNC?.CODE_ANALYSIS || '/api/async/code-analysis',
                {
                    project_id: parseInt(retroProjectId, 10),
                    code_files: [],
                    language: retroLanguage,
                    test_framework: getTestFramework(retroLanguage),
                    generation_type: 'automation',
                }
            )
            toast.success('Automation code generation job submitted.')
            setAutomationDialogOpen(false)
        } catch (err) {
            logger.error('Automation generation failed', { error: err })
            toast.error('Failed to submit automation generation job')
        } finally {
            setIsGeneratingCode(false)
        }
    }

    const getImportanceLabel = (importance: number) => {
        switch (importance) {
            case TestImportance.Low: return 'Low'
            case TestImportance.Medium: return 'Medium'
            case TestImportance.High: return 'High'
            default: return '—'
        }
    }

    const getImportanceBadgeVariant = (importance: number) => {
        switch (importance) {
            case TestImportance.High: return 'destructive' as const
            case TestImportance.Medium: return 'warning' as const
            default: return 'default' as const
        }
    }

    const getExecutionTypeLabel = (et: number) => {
        switch (et) {
            case ExecutionType.Manual: return 'Manual'
            case ExecutionType.Automated: return 'Automated'
            default: return '—'
        }
    }

    const getProjectNames = (tcId: number): string => {
        const m = membershipsCache.get(tcId)
        if (!m) return loadingMemberships.has(tcId) ? '...' : '—'
        const names = [
            ...m.projects_direct.map((p) => p.project_name),
            ...m.projects_via_suites.map((p) => p.project_name),
        ]
        const unique = [...new Set(names)]
        return unique.length > 0 ? unique.join(', ') : '—'
    }

    const getSuiteNames = (tcId: number): string => {
        const m = membershipsCache.get(tcId)
        if (!m) return loadingMemberships.has(tcId) ? '...' : '—'
        return m.suites.length > 0 ? m.suites.map((s) => s.suite_name).join(', ') : '—'
    }

    const genBlockReason = isGenerating
        ? 'Submitting...'
        : !genProjectId ? 'Select a project'
            : !genLayer ? 'Select an architectural layer'
                : !genFile ? 'Select a file'
                    : null

    if (error) {
        return (
            <Card>
                <CardContent className="pt-6">
                    <p className="text-destructive">
                        {error instanceof Error ? error.message : 'Failed to load test cases'}
                    </p>
                </CardContent>
            </Card>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle>Test Cases</CardTitle>
                            <CardDescription>
                                {totalCount} test case{totalCount !== 1 ? 's' : ''} total
                            </CardDescription>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setGenerateDialogOpen(true)}>
                                <Sparkles className="mr-2 h-4 w-4" />
                                AI Generate
                            </Button>
                            <Button variant="outline" onClick={() => setAutomationDialogOpen(true)}>
                                <Code2 className="mr-2 h-4 w-4" />
                                Generate Code
                            </Button>
                            <Button onClick={handleCreate}>
                                <Plus className="mr-2 h-4 w-4" />
                                New Test Case
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center gap-2">
                            <Filter className="h-4 w-4 text-muted-foreground" />
                            <Select
                                value={selectedProjectId}
                                onValueChange={(v) => { setSelectedProjectId(v); setCurrentPage(1) }}
                            >
                                <SelectTrigger className="w-[200px]">
                                    <SelectValue placeholder="All Projects" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Projects</SelectItem>
                                    {projects?.map((p) => (
                                        <SelectItem key={p.id} value={p.id.toString()}>
                                            {p.prefix ? `[${p.prefix}] ` : ''}{p.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="relative flex-1 max-w-sm">
                            <Input
                                placeholder="Search test cases..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : filteredTestCases.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            {searchQuery
                                ? 'No test cases match your search.'
                                : 'No test cases found. Create one to get started.'}
                        </div>
                    ) : (
                        <>
                            <div className="rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Project(s)</TableHead>
                                            <TableHead>Suite(s)</TableHead>
                                            <TableHead>Importance</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Steps</TableHead>
                                            <TableHead className="w-[120px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredTestCases.map((tc) => (
                                            <TableRow
                                                key={tc.id}
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => navigate(`/tests/test-cases/${tc.id}`)}
                                            >
                                                <TableCell className="font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                                        <span className="truncate max-w-[300px]">{tc.name}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]">
                                                    {getProjectNames(tc.id)}
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]">
                                                    {getSuiteNames(tc.id)}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={getImportanceBadgeVariant(tc.importance)}>
                                                        {getImportanceLabel(tc.importance)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={tc.execution_type === ExecutionType.Automated ? 'default' : 'outline'}>
                                                        {getExecutionTypeLabel(tc.execution_type)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{tc.step_count ?? '—'}</TableCell>
                                                <TableCell>
                                                    <div
                                                        className="flex items-center gap-1"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Button variant="ghost" size="icon" onClick={() => handleEdit(tc)} title="Edit">
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDeleteClick(tc.id, tc.name)}
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {totalPages > 1 && (
                                <div className="flex items-center justify-between mt-4">
                                    <p className="text-sm text-muted-foreground">
                                        Page {currentPage} of {totalPages} ({totalCount} total)
                                    </p>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={currentPage <= 1}
                                            onClick={() => setCurrentPage((p) => p - 1)}
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={currentPage >= totalPages}
                                            onClick={() => setCurrentPage((p) => p + 1)}
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <TestCaseDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                testCase={selectedTestCase}
                mode={dialogMode}
            />

            <ConfirmDialog
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
                title="Delete Test Case"
                description={`Are you sure you want to delete "${testCaseToDelete?.name}"? This action cannot be undone.`}
                onConfirm={handleDeleteConfirm}
                loading={deleteMutation.isPending}
                variant="destructive"
            />

            {/* AI Test Generation Dialog */}
            <Dialog open={generateDialogOpen} onOpenChange={handleGenerateDialogClose}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>AI Test Generation</DialogTitle>
                        <DialogDescription>
                            Upload source code to generate test cases automatically. Test suites are created and organized automatically based on the architectural layer and module name.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Project</Label>
                            <Select value={genProjectId} onValueChange={setGenProjectId}>
                                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                                <SelectContent>
                                    {projects?.map((p) => (
                                        <SelectItem key={p.id} value={p.id.toString()}>
                                            {p.prefix ? `[${p.prefix}] ` : ''}{p.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Architectural Layer</Label>
                            <Select value={genLayer} onValueChange={setGenLayer}>
                                <SelectTrigger><SelectValue placeholder="Select layer" /></SelectTrigger>
                                <SelectContent>
                                    {ARCHITECTURAL_LAYERS.map((l) => (
                                        <SelectItem key={l} value={l}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                Used to organize test suites — e.g. "Backend - User Authentication"
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Language</Label>
                            <Select value={genLanguage} onValueChange={setGenLanguage}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {['python','javascript','typescript','rust','go','java','csharp','ruby','php','c','cpp'].map((l) => (
                                        <SelectItem key={l} value={l}>
                                            {l.charAt(0).toUpperCase() + l.slice(1)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Source File</Label>
                            <input
                                type="file"
                                accept=".zip,.tar,.tar.gz,.tgz,.py,.js,.ts,.rs,.go,.java,.cs,.rb,.php,.c,.cpp,.h,.hpp"
                                onChange={(e) => setGenFile(e.target.files?.[0] ?? null)}
                                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            />
                            {genFile && (
                                <p className="text-xs text-green-600">Selected: {genFile.name}</p>
                            )}
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="edge-cases"
                                    checked={genEdgeCases}
                                    onCheckedChange={(c) => setGenEdgeCases(!!c)}
                                />
                                <Label htmlFor="edge-cases">Edge Cases</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="negative-tests"
                                    checked={genNegativeTests}
                                    onCheckedChange={(c) => setGenNegativeTests(!!c)}
                                />
                                <Label htmlFor="negative-tests">Negative Tests</Label>
                            </div>
                        </div>
                        {genBlockReason && (
                            <p className="text-xs text-amber-600">⚠ {genBlockReason} to enable generation</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => handleGenerateDialogClose(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={handleGenerateTests} disabled={!!genBlockReason}>
                            {isGenerating
                                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>
                                : 'Generate Tests'
                            }
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Automation Code Generation Dialog */}
            <Dialog open={automationDialogOpen} onOpenChange={setAutomationDialogOpen}>
                <DialogContent className="sm:max-w-[450px]">
                    <DialogHeader>
                        <DialogTitle>Generate Automation Code</DialogTitle>
                        <DialogDescription>
                            Generate executable test automation code for existing manual test cases.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Project</Label>
                            <Select value={retroProjectId} onValueChange={setRetroProjectId}>
                                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                                <SelectContent>
                                    {projects?.map((p) => (
                                        <SelectItem key={p.id} value={p.id.toString()}>
                                            {p.prefix ? `[${p.prefix}] ` : ''}{p.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Target Language</Label>
                            <Select value={retroLanguage} onValueChange={setRetroLanguage}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {['python','javascript','typescript','rust','go','java','csharp'].map((l) => (
                                        <SelectItem key={l} value={l}>
                                            {l.charAt(0).toUpperCase() + l.slice(1)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setAutomationDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={handleGenerateAutomation} disabled={isGeneratingCode || !retroProjectId}>
                            {isGeneratingCode
                                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>
                                : 'Generate Code'
                            }
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}